/** Sample content for the Resources pages (blog, reports, publications, infographics). */

import type { ArticleItem, DownloadItem, GalleryItem } from '@components/pages/ResourceSections';

const ARTICLE_IMAGES = [
    '/landing/blog/blog1.webp',
    '/landing/blog/blog2.webp',
    '/landing/blog/blog3.webp',
    '/landing/stories/story1.webp',
    '/landing/stories/story2.webp',
    '/landing/stories/story3.webp',
    '/landing/stories/story4.webp',
    '/landing/video/gallery-left.webp',
    '/landing/video/gallery-mid-top.webp',
    '/landing/video/gallery-mid-bottom.webp',
    '/landing/video/gallery-right.webp',
    '/landing/problem/secondary-image.webp',
];

const AVATARS = ['/landing/report/avatar-1.webp', '/landing/report/avatar-2.webp', '/landing/report/avatar-3.webp'];

const AUTHORS = ['Sylvester Agih', 'Rita Odafe', 'Ada Okafor'];

/** Headline, standfirst and body for each article, in the order of ARTICLE_IMAGES. */
const ARTICLE_COPY: { title: string; excerpt: string; quote: string; body: string[] }[] = [
    {
        title: 'Eight months in a cell for a case that was never filed',
        excerpt:
            'How a single missing charge sheet kept a man in pre-trial detention, and what it took to get him out.',
        quote: 'A case does not need to be defended before it can be progressed. It needs someone to ask where it is.',
        body: [
            'The file had been sitting in a registry drawer since March. No charge sheet, no listing, no application for bail — and, crucially, nobody whose job it was to notice. By the time one of our duty solicitors pulled the docket during a routine station visit, the man named on it had been in custody for eight months on a matter that had never formally begun.',
            'This is not an unusual story, and that is the point. The single largest driver of time spent in pre-trial detention is not the seriousness of the allegation or the complexity of the evidence. It is the absence of anyone with the standing and the time to move the paperwork along.',
            'What changed here was mundane. A lawyer requested the file, established that no charge had been laid, and filed an application. The court listed it within the fortnight. Three weeks after the docket was located, he went home. No new law was passed and no precedent was set — a step that should have happened in March simply happened, late.',
            'We record these cases because the pattern only becomes visible in aggregate. One missing charge sheet is an administrative slip. Several hundred of them, clustered in particular registries and particular months, is a systems failure with a shape you can measure, publish and argue about.',
            'The uncomfortable part is what this says about the cases we never see. Every file our solicitors happen to pull is one they were physically present to find. The ones nobody visits are, by definition, absent from the numbers.',
        ],
    },
    {
        title: 'What we found when we asked 36 high courts how long they take',
        excerpt: 'Some states answered in full. Others refused outright. The gaps turned out to be the story.',
        quote: 'A refusal to publish is not an absence of data. It is a decision, and it belongs in the findings.',
        body: [
            'The request was deliberately modest: for a defined period, how many matters were filed, how many were disposed of, and how long the average case took from filing to judgment. Nothing that required new record-keeping. Nothing a registry does not already hold in some form.',
            'The responses split into three groups. A minority answered in full, with figures broken down by division. A larger group answered partially — totals without timescales, or timescales without totals. The rest did not respond at all, or declined on grounds that did not survive a second reading.',
            'It would be easy to treat the non-responses as missing data and quietly drop them. We do the opposite. A court that will not say how long it takes is telling you something, and the reader deserves to see that alongside the courts that did answer.',
            'Where we could triangulate — through cause lists, sampled files and practitioner interviews — we did, and we label those figures as estimates rather than returns. The distinction matters. An estimate can be argued with; a fabricated precision cannot.',
            'The useful finding was not which court came top. It was how strongly willingness to publish tracked with performance. The courts confident enough to hand over their numbers were, with few exceptions, the courts whose numbers were worth handing over.',
        ],
    },
    {
        title: 'The adjournment problem nobody measures',
        excerpt: 'A hearing that does not sit costs a litigant a day of wages. We counted how often it happens.',
        quote: 'Every adjournment is a transfer of cost from the court’s calendar to somebody’s household budget.',
        body: [
            'Adjournments are recorded as procedural events. In the register they occupy a single line and carry no price. For the person who travelled to court, took the day off and paid for transport, they are the most expensive thing that happened that month.',
            'We began counting them because nobody else was. Not the number of adjournments a court grants in total, which tells you little, but the number a single matter accumulates before it is heard, and the reason recorded for each one.',
            'The reasons cluster more tightly than expected. A small number of causes — counsel unavailable, file not produced, court not sitting — account for the overwhelming majority. These are scheduling and records problems, not legal ones, which is precisely why they are tractable.',
            'The cost side is harder and we are still refining it. A day lost is not the same for a salaried worker as for a trader who cannot open her stall, and averaging the two produces a number that flatters the system.',
            'What we can say already is that the burden is not evenly distributed. Matters involving unrepresented parties accumulate adjournments faster, which means the people least able to absorb the cost are the ones paying it most often.',
        ],
    },
    {
        title: 'Why legal aid fails at the last mile — and how to fix it',
        excerpt: 'Free representation is worthless if the person entitled to it never hears that it exists.',
        quote: 'The scheme was not underused because it was unwanted. It was underused because it was unknown.',
        body: [
            'Legal aid schemes are usually assessed on capacity: how many lawyers, how many matters, how much funding. Those numbers can look healthy while the scheme is failing, because they measure supply and say nothing about whether the supply is reaching anyone.',
            'When we traced how people actually found their way to free representation, almost none had done so through an official channel. They heard about it from a relative, a market association, a religious leader, or someone who had been through it themselves.',
            'That has an obvious implication and an uncomfortable one. The obvious one is that outreach should run through the networks people already trust. The uncomfortable one is that a scheme relying on word of mouth reaches whoever happens to be well connected, and systematically misses those who are not.',
            'So the fix is not a better website. It is putting trained people inside the networks that already exist — market associations, community health workers, religious congregations — and giving them a route to hand someone directly to a lawyer rather than to a phone number.',
            'The measure we care about is not how many people started a request. It is how many completed one, and whether the people completing them look like the people the scheme was designed for.',
        ],
    },
    {
        title: 'She reported it three times before anyone opened a file',
        excerpt: 'What survivors of gender-based violence run into long before a case ever reaches a courtroom.',
        quote: 'By the time a case is described as weak, the reasons it is weak have usually already been recorded.',
        body: [
            'Cases of sexual and gender-based violence are frequently described as difficult to prove. That framing does a lot of quiet work, because it locates the problem at trial — where the evidence is weighed — rather than at the points where the evidence was, or was not, collected.',
            'Follow a case backwards from the courtroom and the failures are earlier and more mundane. A first report not written down. A medical examination delayed past the point of usefulness. A statement taken in a room with no privacy, in a language the complainant does not use at home.',
            'None of these are legal problems. They are process problems, and each one is fixable by someone with authority over a form, a room or a rota. That is why our work here concentrates on the first seventy-two hours rather than on advocacy at trial.',
            'It also changes what support means. Staying with a survivor through a case is not a single act of representation; it is showing up repeatedly, at each of the points where the case is most likely to quietly stop.',
            'We measure this the same way we measure everything else: not by cases opened, but by cases that reached a hearing, and by how many did so with the evidence that should have been gathered on the first day.',
        ],
    },
    {
        title: 'Community paralegals are doing the work the system skips',
        excerpt: 'Trained neighbours, not lawyers, are often the first and only legal help a household ever gets.',
        quote: 'They are trusted because they were already there — and that trust does work no institution can buy.',
        body: [
            'A community paralegal is not a substitute for a lawyer, and the good ones are the first to say so. What they are is present, local, and known — three qualities the formal justice system almost never manages at the same time.',
            'Most of what they handle never becomes a case. A tenancy dispute settled before it escalates, a wage withheld and then paid, a document explained to someone who was about to sign it. These are the matters that would otherwise either disappear or arrive in court far more expensive than they needed to be.',
            'The point at which the model breaks is referral. A paralegal who identifies a matter needing a lawyer, and can only offer a phone number, has done half a job. Whether the other half happens depends on the person having the money, the confidence and the day off to follow it up.',
            'Closing that gap is unglamorous engineering and unglamorous logistics: a way to hand a matter directly to a solicitor, with the facts already recorded, and a way to see whether it was picked up.',
            'The training matters too, and it cuts both ways. Paralegals need to know enough law to be useful and enough of its limits to know when to stop — and to say so plainly rather than guess.',
        ],
    },
    {
        title: 'Open court data is not a technology problem',
        excerpt: 'Most registries already hold the numbers. Publishing them is a decision, not an engineering task.',
        quote: 'The data exists. What is missing is a decision that it belongs to the public rather than the registry.',
        body: [
            'Every proposal to open up court data eventually arrives at a discussion about systems: databases, portals, integrations, procurement. That discussion is usually premature, because it assumes the constraint is technical.',
            'In most registries we have worked with, the constraint is not technical. The figures are already recorded, often on paper, often accurately. What is absent is any obligation to publish them, any standard format to publish them in, and any consequence for not doing so.',
            'A well-run registry with a paper ledger can publish a monthly summary. A poorly run one with expensive software can decline to. The determining variable is the decision, not the tooling.',
            'That is why we spend more time on a publication standard than on a platform. A short, boring specification — these fields, this cadence, this format — is worth more than a portal nobody is required to fill.',
            'Where courts do adopt it, the second-order effects appear quickly. Registries start noticing their own bottlenecks, because for the first time somebody is looking at the aggregate rather than the individual file.',
        ],
    },
    {
        title: 'Building legal tools for a phone with no data left',
        excerpt: 'Design notes from shipping Podus to users on low-end handsets and intermittent connections.',
        quote: 'If your product needs a good connection to help someone, it will help the people who least need it.',
        body: [
            'The default assumptions of modern web development — a recent device, a stable connection, an unmetered data plan — quietly select for users who already have options. For a legal-aid product, that is close to selecting against your entire intended audience.',
            'So the constraints came first. The initial page weight target was set low enough to be uncomfortable, every screen has to be usable on a small, old display, and any flow that cannot survive a dropped connection mid-way has to be redesigned rather than excused.',
            'Some of the consequences were obvious: aggressive image budgets, no heavy client-side frameworks on the entry paths, forms that save partial state locally. Others were less so. Long forms, for instance, are better split across more steps, not fewer, because each completed step is progress that survives a disconnection.',
            'Language turned out to matter as much as bytes. A screen that loads in a second and then asks a question in unfamiliar legal register has not actually reached anyone.',
            'None of this is novel engineering. It is simply a decision to treat the worst realistic conditions as the design target rather than as an edge case to be handled later.',
        ],
    },
    {
        title: 'When AI should say “ask a lawyer”',
        excerpt: 'The hardest part of legal automation is teaching it where its competence ends.',
        quote: 'A confident wrong answer to a legal question is worse than no answer, because it will be acted on.',
        body: [
            'The appealing version of legal AI answers everything. The useful version knows what it should not answer, and says so early enough to be helpful rather than after the user has already relied on it.',
            'The distinction is not about difficulty. Plenty of hard questions have settled answers, and plenty of simple-sounding ones do not — because they turn on facts the system has not been told, on a jurisdiction it cannot infer, or on a point that is genuinely unresolved.',
            'So the design question is not how to make the model more confident. It is how to detect the categories where confidence is inappropriate, and route them to a person before an answer is generated at all.',
            'That has a cost. Handing over to a human is slower and more expensive than answering, and every handover is a place where a user can drop out. We accept that cost because the alternative is a tool whose failures land hardest on people with no way to check it.',
            'It also sets the honest boundary of what this kind of product is for. It can carry someone a long way through the ordinary and the procedural. It should not be the last thing that speaks to them about a matter that decides their liberty or their livelihood.',
        ],
    },
    {
        title: 'A year of the Justice Clock',
        excerpt: 'What twelve months of counting delay taught us about which courts change and which do not.',
        quote: 'Measurement alone changes very little. Measurement that somebody is answerable for changes a great deal.',
        body: [
            'The premise was simple enough to be tested: start a clock when a matter is filed, stop it when judgment is delivered, and publish the interval. A year in, the mechanics work. The more interesting question is what happened in the courts being counted.',
            'Broadly, three responses. A small group engaged with the figures directly, queried the methodology, and in several instances used the output to argue internally for more listing days. A larger group acknowledged the numbers and did nothing observable. A third group disputed the exercise itself.',
            'The disputes were often useful. Where a court could show that our figures misclassified a category of matter, we corrected them and said so — which, over time, made the disputes shorter and the data better.',
            'What did not work was publication on its own. Where no one inside the system was accountable for the number, it changed at roughly the rate it would have changed anyway.',
            'That is the main lesson of the first year, and it is a limiting one. The clock is a necessary instrument, not a sufficient intervention. It creates the conditions for pressure; it does not supply the pressure.',
        ],
    },
    {
        title: 'The contract you signed was not written for you',
        excerpt: 'Plain-language drafting is not a nicety. It decides who can enforce their own agreement.',
        quote: 'An agreement only one party can read is an agreement only one party can enforce.',
        body: [
            'Standard-form contracts are drafted by the party with the most to lose from ambiguity, which is almost never the party signing at the counter. The result is a document that is precise, enforceable, and — for the person bound by it — largely opaque.',
            'The consequence is not usually dramatic. It is a slow asymmetry: one side knows what the termination clause does, the other discovers it at the point of termination.',
            'Plain-language drafting is often treated as a courtesy, a readability improvement layered on top of the real legal text. It is better understood as a question of who can actually use the agreement they signed.',
            'In practice this means fewer defined terms, shorter sentences, and — most importantly — an explanation attached to each clause that says what it does rather than restating it. That last part is what most templates omit.',
            'None of it weakens enforceability. A clause a court will uphold and a clause a signatory can understand are not in tension; they have simply been treated as separate concerns for a long time.',
        ],
    },
    {
        title: 'Reform moves at the speed of evidence',
        excerpt: 'How a data set becomes a practice direction, and why most findings never make that journey.',
        quote: 'Evidence that never reaches a decision-maker has not finished its job. It has only finished ours.',
        body: [
            'Most research about court performance ends at publication. A report is launched, covered briefly, cited occasionally, and then joins the pile. The finding was correct and nothing happened, which is the ordinary outcome rather than the exceptional one.',
            'The cases where something did happen have a common shape. The finding was narrow enough to act on, it landed with somebody who had the authority to act, and it arrived at a moment when that person was already looking for a reason.',
            'That third condition is the one researchers control least and think about least. A rules committee mid-review is a different audience from the same committee six months later, and the same data set will succeed with one and fail with the other.',
            'It has changed how we plan work. We now ask, before starting a study, who would have to act on the answer and what they would need it to look like. If we cannot name that person, the study is usually not worth running yet.',
            'This is not a case for softening findings to suit an audience. It is a case for spending as much effort on the last mile of evidence as on the first — because a finding nobody can act on is, in practice, indistinguishable from no finding at all.',
        ],
    },
];

export const ARTICLES: ArticleItem[] = ARTICLE_IMAGES.map((image, i) => ({
    slug: `article-${i + 1}`,
    image,
    title: ARTICLE_COPY[i].title,
    author: AUTHORS[i % AUTHORS.length],
    avatar: AVATARS[i % AVATARS.length],
    excerpt: ARTICLE_COPY[i].excerpt,
    quote: ARTICLE_COPY[i].quote,
    body: ARTICLE_COPY[i].body,
}));

export const getArticle = (slug?: string): ArticleItem | undefined => ARTICLES.find((a) => a.slug === slug);

/** Hero card of the "Top Article" split. */
export const TOP_ARTICLE: ArticleItem = {
    ...ARTICLES[0],
    image: '/landing/stories/story2.webp',
    author: 'Rita Odafe',
    avatar: '/landing/report/avatar-1.webp',
};

/** Right-hand rail of the "Top Article" split. */
export const RECENT_RAIL: ArticleItem[] = [2, 3, 4].map((n, i) => ({
    slug: `article-${n}`,
    image: ARTICLE_IMAGES[n - 1],
    title: ARTICLE_COPY[n - 1].title,
    author: AUTHORS[i % AUTHORS.length],
    avatar: AVATARS[i % AVATARS.length],
    excerpt: ARTICLE_COPY[n - 1].excerpt,
}));

const COVERS = ['/landing/report/thumb-1.webp', '/landing/report/thumb-2.webp', '/landing/report/thumb-3.webp'];

/** Titles for the downloadable publications, reports and infographics. */
const DOWNLOAD_TITLES = [
    'Measuring the Pace of Justice Delivery Through Data — 2020 Edition',
    'Executive Patronage and Judicial Autonomy: A Decade of Gifts from Governors to the Bench',
    'Review of the Judicial Code of Conduct and Infractions',
    'Half a Year, Full Impact: Citizens’ Gavel Mid-Year Review',
    'Pre-Trial Detention in Nigeria: Who Waits, and For How Long',
    'The Cost of an Adjournment: Counting What Litigants Lose',
    'Freedom of Information and the Courts: A Compliance Scorecard',
    'Access to Justice at the Last Mile: Lessons from Community Paralegals',
    'Sexual and Gender-Based Violence: From First Report to Trial',
    'Open Court Data: A Practical Standard for Nigerian Registries',
    'Legal Aid Uptake and the Information Gap',
    'State High Court Performance: Five-Year Trend Analysis',
];

/** Turn a title into the anchor a shared card link points at. */
const slugify = (text: string): string =>
    text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 60);

export const DOWNLOADS: DownloadItem[] = DOWNLOAD_TITLES.map((text, i) => ({
    cover: COVERS[i % COVERS.length],
    text,
    slug: slugify(text),
}));

export const RECENT_ARTICLES = ARTICLES.slice(0, 3);

/* ── Stories ──────────────────────────────────────────────────────────── */

/**
 * Stories are written up by our own team from case files, and the people in
 * them are described rather than named. Nothing here is a quote attributed to
 * an identifiable person — that only happens with a consent record on file.
 */
const STORY_COPY: { title: string; excerpt: string; quote: string; body: string[] }[] = [
    {
        title: 'She sold vegetables outside the court that would not hear her',
        excerpt:
            'A trader in Ibadan spent two years in a land dispute she could not afford to attend. It took one filing to end it.',
        quote: 'The hearing she could not afford to attend was the one that would have ended it.',
        body: [
            'The stall was four minutes from the courthouse. That detail matters, because for two years the distance between her and a resolution was never geographic. Every listing meant a day not trading, transport for a witness, and a fee she could not predict in advance.',
            'The dispute itself was narrow. A neighbour had extended a wall across a boundary that a 1998 survey put plainly in her favour. There was no serious question of fact; there was only the question of who would keep showing up.',
            'She stopped showing up in the second year, which in practice decides most matters like this one. A case does not have to be lost to be over.',
            'What changed was mundane. A paralegal working the market that month took the file, found the survey, and filed an application to have the matter heard on the documents. It was listed in five weeks and decided in one sitting.',
            'We publish these because the pattern is the point. She did not need a better argument. She needed the cost of pursuing the argument she already had to fall below the value of the land.',
        ],
    },
    {
        title: 'Eleven months on remand, and no charge sheet in the file',
        excerpt:
            'A routine station visit turned up a docket that had never formally begun — and a man still in custody because of it.',
        quote: 'Nobody had refused him bail. Nobody had considered it.',
        body: [
            'The docket had been opened in a hurry and then not touched. No charge sheet, no listing, no bail application on the record. He had been in custody since the previous rainy season on a matter that, procedurally, had never started.',
            'The people who could have noticed were not being negligent in any dramatic sense. The registry holds thousands of files; the officers rotate; there is no one whose job it is to ask which of them have stopped moving.',
            'That is the failure worth naming. Not a decision made badly, but a decision never reached — which is harder to appeal, harder to count, and considerably more common.',
            'Once the file was located, the sequence took nineteen days: a request for the docket, a finding that no charge had been laid, an application, a listing, and a release.',
            'We record the elapsed time on every case like this because the aggregate is the only version of the story that changes anything. One overlooked file is an accident. Two hundred, clustered in the same three registries, is a system doing exactly what it is built to do.',
        ],
    },
    {
        title: 'The report she filed in March was finally read in September',
        excerpt:
            'What happens to a sexual violence complaint between the counter and the courtroom, traced through one file.',
        quote: 'Every handoff was legitimate. The delay was the sum of them.',
        body: [
            'She reported in March, at a station that took the complaint properly and referred it on. Nothing in what followed was refusal. The file moved from the station to a state prosecutor, from that desk to a colleague on transfer, and from there into a queue.',
            'Each of those steps was defensible on its own terms. None of them had a clock attached, which is how six months passes without anyone having decided that six months was acceptable.',
            'The medical evidence, gathered within seventy-two hours, held up. The witness did not withdraw. By the standards usually applied to these matters, this was a strong case that simply sat.',
            'Our caseworker was assigned in September, and the first thing she did was ask, in writing, where the file was. That question — asked by somebody with standing to ask it — moved the matter more in a fortnight than the previous half-year had.',
            'The uncomfortable reading is that the difference was not legal skill. It was attention. Which means the outcome for anyone without an advocate is not a worse result; it is usually no result at all.',
        ],
    },
    {
        title: 'A market association learned to read its own contracts',
        excerpt: 'Forty traders, one afternoon, and a supply agreement none of them had been able to get through.',
        quote: 'An agreement only one party can read is an agreement only one party can enforce.',
        body: [
            'The agreement ran to nine pages. It set out delivery windows, penalties for short supply, and a termination clause that took effect on fourteen days notice — which several signatories learned about at the point of termination.',
            'None of this was hidden. It was simply written for a reader who does not exist on the buying side of a market stall: someone with time, a legal vocabulary, and no goods to move that morning.',
            'The session did not produce a better contract. It produced forty people who could point at the clause that would cost them and say what it did, which turned out to be the more useful outcome.',
            'Three of them renegotiated the notice period within the month. One walked away from a renewal. That is a small return on an afternoon, and it is more than the previous two years of the same agreement had produced.',
            'We keep running these because the alternative — reviewing each contract individually, for free, forever — does not scale, and because a trader who can read the clause does not need us next time.',
        ],
    },
];

/** Story photography, in the order of STORY_COPY. */
const STORY_IMAGES = [
    '/landing/stories/story1.webp',
    '/landing/stories/story2.webp',
    '/landing/stories/story3.webp',
    '/landing/stories/story4.webp',
];

export const STORIES: ArticleItem[] = STORY_COPY.map((copy, i) => ({
    slug: `story-${i + 1}`,
    image: STORY_IMAGES[i % STORY_IMAGES.length],
    title: copy.title,
    author: AUTHORS[i % AUTHORS.length],
    avatar: AVATARS[i % AVATARS.length],
    excerpt: copy.excerpt,
    quote: copy.quote,
    body: copy.body,
}));

export const getStory = (slug?: string): ArticleItem | undefined => STORIES.find((s) => s.slug === slug);

/** Hero card of the Stories page's "Top Story" split. */
// Copies, not references into STORIES: the CMS places these rails independently,
// so sharing the objects would have the top story overwrite the first grid card.
export const TOP_STORY: ArticleItem = { ...STORIES[0] };

/** Right-hand rail beside the top story. */
export const RECENT_STORIES: ArticleItem[] = STORIES.slice(1, 4).map((s) => ({ ...s }));

/* ── Gallery ──────────────────────────────────────────────────────────── */

const GALLERY_VIDEOS: { title: string; caption: string; duration: string; image: string }[] = [
    {
        title: 'Inside Justice Clock: how a state publishes its own case data',
        caption: 'A walk through the dashboard with the Ogun State Ministry of Justice team.',
        duration: '6:41',
        image: '/landing/video/video-hero.webp',
    },
    {
        title: 'A day with the duty solicitors in Ibadan',
        caption: 'Station visits, docket checks, and the paperwork that decides who goes home.',
        duration: '4:18',
        image: '/landing/video/gallery-left.webp',
    },
    {
        title: 'Podus AI, explained in four minutes',
        caption: 'What the assistant does, what it refuses to do, and why that line matters.',
        duration: '3:52',
        image: '/landing/video/gallery-mid-top.webp',
    },
    {
        title: 'Community paralegals at the last mile',
        caption: 'Training week in Oyo State, and the first cases the cohort took on.',
        duration: '8:07',
        image: '/landing/video/gallery-right.webp',
    },
];

const GALLERY_IMAGES: { title: string; caption: string; image: string }[] = [
    {
        title: 'Legal aid clinic, Bodija Market',
        caption: 'Forty-two matters opened over two days, most of them tenancy and supply disputes.',
        image: '/landing/stories/story1.webp',
    },
    {
        title: 'Justice Clock launch, Abeokuta',
        caption: 'The Ministry of Justice publishes its first quarter of disposal figures.',
        image: '/landing/stories/story2.webp',
    },
    {
        title: 'Paralegal cohort, Oyo State',
        caption: 'Certification day for the fourth intake of community paralegals.',
        image: '/landing/stories/story3.webp',
    },
    {
        title: 'Court observation training',
        caption: 'Volunteers learning the observation protocol before their first sitting.',
        image: '/landing/stories/story4.webp',
    },
    {
        title: 'Mid-year review, Lagos',
        caption: 'Partners and state counsel reviewing the first half of the year.',
        image: '/landing/video/gallery-mid-bottom.webp',
    },
    {
        title: 'Roundtable on open court data',
        caption: 'Registrars, researchers and engineers on what a publishable standard needs.',
        image: '/landing/problem/secondary-image.webp',
    },
];

/**
 * Gallery items. Videos carry a poster and a runtime; the embed URL itself is
 * CMS-supplied, so a video with no link yet opens as its poster frame.
 */
export const GALLERY: GalleryItem[] = [
    ...GALLERY_VIDEOS.map((v, i) => ({
        slug: `video-${i + 1}`,
        kind: 'video' as const,
        image: v.image,
        title: v.title,
        caption: v.caption,
        duration: v.duration,
    })),
    ...GALLERY_IMAGES.map((g, i) => ({
        slug: `image-${i + 1}`,
        kind: 'image' as const,
        image: g.image,
        title: g.title,
        caption: g.caption,
    })),
];

/* ── Documents, per collection ────────────────────────────────────────── */

export type DocCollection = 'publications' | 'reports' | 'infographics';

export const DOC_COLLECTIONS: DocCollection[] = ['publications', 'reports', 'infographics'];

/**
 * Each downloads page reads its own collection. Until the API answers they all
 * hold the same bundled sample set, which is what these pages showed before —
 * the CMS is what tells them apart, and it fills these in at startup.
 *
 * Deep copies, not `[...DOWNLOADS]`: the hydration merges into the element
 * objects in place, so three arrays sharing one set of objects would end up
 * showing whichever collection was merged last.
 */
export const DOCUMENTS: Record<DocCollection, DownloadItem[]> = {
    publications: DOWNLOADS.map((d) => ({ ...d })),
    reports: DOWNLOADS.map((d) => ({ ...d })),
    infographics: DOWNLOADS.map((d) => ({ ...d })),
};

/** The Top + Recent split above each downloads grid, per collection. */
export const DOC_TOP: Record<DocCollection, ArticleItem> = {
    publications: { ...TOP_ARTICLE },
    reports: { ...TOP_ARTICLE },
    infographics: { ...TOP_ARTICLE },
};

export const DOC_RECENT: Record<DocCollection, ArticleItem[]> = {
    publications: RECENT_RAIL.map((r) => ({ ...r })),
    reports: RECENT_RAIL.map((r) => ({ ...r })),
    infographics: RECENT_RAIL.map((r) => ({ ...r })),
};

/** Reads the collection a downloads route is showing from its base path. */
export const collectionOf = (basePath: string): DocCollection => {
    const last = basePath.split('/').filter(Boolean).pop() ?? '';
    return (DOC_COLLECTIONS as string[]).includes(last) ? (last as DocCollection) : 'publications';
};
